import { Button, Card } from "@rumbledirect/ui";
import { Tag, Timeline } from "antd";
import { Claim } from "@rumble-it/common-lib/types/models/Claim.interface";
import { SyncOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";

interface ClaimDossierOverviewTimelineCardProps {
  claim?: Claim | null;
  isLoading: boolean;
}

export const ClaimDossierOverviewTimelineCard = ({
  claim,
  isLoading,
}: ClaimDossierOverviewTimelineCardProps) => {
  const baseStatusFlow = [
    { key: "concept", label: "Nieuw dossier" },
    { key: "claim_verstuurd", label: "In afwachting van calculatie" },
  ];

  const expertiseStatuses = [
    {
      key: "calculatie_expertise_aangevraagd",
      label: "In afwachting van expertise",
    },
    { key: "calculatie_expertise_ontvangen", label: "Expertise ontvangen" },
  ];

  const finalStatus = {
    key: "calculatie_goedgekeurd",
    label: "Calculatie beoordeeld",
  };

  const currentStatus = claim?.status;
  const calculations = claim?.calculations || [];

  // Check for calculations with specific intents
  const hercalculatieCalculations = calculations.filter(
    (calc) => calc.calculationIntent === "Hercalculatie",
  );
  const expertiseCalculations = calculations.filter(
    (calc) => calc.calculationIntent === "Expertise",
  );

  const shouldShowRecalculation =
    hercalculatieCalculations.length > 0 ||
    (calculations.length > 0 && currentStatus === "claim_verstuurd");
  const shouldShowExpertise =
    expertiseCalculations.length > 0 ||
    currentStatus === "calculatie_expertise_aangevraagd" ||
    currentStatus === "calculatie_expertise_ontvangen";

  let statusFlow = [...baseStatusFlow];

  // Add calculatie_ontvangen step
  statusFlow = [
    ...statusFlow,
    { key: "calculatie_ontvangen", label: "Calculatie beoordelen" },
  ];

  // Add recalculation steps for each hercalculatie calculation (comes first)
  if (shouldShowRecalculation) {
    if (hercalculatieCalculations.length > 0) {
      // Add steps for actual hercalculatie calculations
      hercalculatieCalculations.forEach((_, index) => {
        statusFlow = [
          ...statusFlow,
          {
            key: `claim_verstuurd_recalc_${index}`,
            label: "In afwachting van hercalculatie",
          },
          {
            key: `calculatie_ontvangen_recalc_${index}`,
            label: "Hercalculatie ontvangen",
          },
        ];
      });
    } else if (calculations.length > 0 && currentStatus === "claim_verstuurd") {
      // Add a single hercalculatie step when waiting for hercalculatie
      statusFlow = [
        ...statusFlow,
        {
          key: "claim_verstuurd_recalc_0",
          label: "In afwachting van hercalculatie",
        },
        {
          key: "calculatie_ontvangen_recalc_0",
          label: "Hercalculatie ontvangen",
        },
      ];
    }
  }

  // Add expertise statuses if expertise calculations exist (comes after hercalculatie)
  if (shouldShowExpertise) {
    statusFlow = [...statusFlow, ...expertiseStatuses];
  }

  // Always add final status at the end
  statusFlow = [...statusFlow, finalStatus];

  let currentStatusIndex = -1;

  // Find current status index based on the claim status
  if (currentStatus) {
    if (
      currentStatus === "calculatie_afgekeurd" ||
      currentStatus === "calculatie_goedgekeurd"
    ) {
      currentStatusIndex = statusFlow.findIndex(
        (status) => status.key === "calculatie_goedgekeurd",
      );
    } else if (currentStatus === "calculatie_expertise_aangevraagd") {
      currentStatusIndex = statusFlow.findIndex(
        (status) => status.key === "calculatie_expertise_aangevraagd",
      );
    } else if (currentStatus === "calculatie_expertise_ontvangen") {
      currentStatusIndex = statusFlow.findIndex(
        (status) => status.key === "calculatie_expertise_ontvangen",
      );
    } else if (
      currentStatus === "calculatie_ontvangen" &&
      shouldShowRecalculation
    ) {
      // If we have hercalculatie and status is calculatie_ontvangen,
      // find the last hercalculatie ontvangen step
      const lastHercalculatieIndex = statusFlow
        .map((status, index) => ({ status, index }))
        .filter((item) =>
          item.status.key.includes("calculatie_ontvangen_recalc"),
        )
        .pop()?.index;

      currentStatusIndex =
        lastHercalculatieIndex ??
        statusFlow.findIndex((status) => status.key === currentStatus);
    } else if (currentStatus === "claim_verstuurd" && shouldShowRecalculation) {
      // If we have hercalculatie and status is claim_verstuurd,
      // find the last hercalculatie verstuurd step
      const lastHercalculatieVerstuurdIndex = statusFlow
        .map((status, index) => ({ status, index }))
        .filter((item) => item.status.key.includes("claim_verstuurd_recalc"))
        .pop()?.index;

      currentStatusIndex =
        lastHercalculatieVerstuurdIndex ??
        statusFlow.findIndex((status) => status.key === currentStatus);
    } else {
      currentStatusIndex = statusFlow.findIndex(
        (status) => status.key === currentStatus,
      );
    }
  }

  const timelineItems = statusFlow.map((status, index) => {
    let color: string | undefined;

    if (currentStatusIndex === -1) {
      color = index === 0 ? undefined : "gray";
    } else {
      if (index < currentStatusIndex) {
        color = "green";
      } else if (index === currentStatusIndex) {
        color = undefined;
      } else {
        color = "gray";
      }
    }

    return {
      children: status.label,
      ...(color && { color }),
    };
  });

  const queryClient = useQueryClient();

  return (
    <Card
      loading={isLoading}
      className="w-full"
      title="Status"
      extra={
        claim?.status === "calculatie_goedgekeurd" ? (
          <Tag color="green">Calculatie goedgekeurd</Tag>
        ) : claim?.status === "calculatie_afgekeurd" ? (
          <Tag color="red">Calculatie afgekeurd</Tag>
        ) : (
          <Button
            onClick={() => queryClient.resetQueries()}
            icon={<SyncOutlined />}
          />
        )
      }
    >
      <Timeline style={{ marginBottom: -25 }} items={timelineItems} />
    </Card>
  );
};
